import { Condition } from 'src/components/Filters/DIConditionInputFilter';
import { FilterOperation } from 'src/types/reportingTypes';
import { TAB_COLUMNS, SEARCH_FIELD_OPTIONS } from './constants';
import { DETECTION_FILTER_CONDITIONS } from './constants';

export function caseCloudTypeInfo(cloudType: string): string {
	let connectorText;
	switch (cloudType.toLowerCase()) {
		case 'onedriveconnector':
		case 'onedrive':
			connectorText = 'OneDrive';
			break;
		case 'sharepointconnector':
		case 'sharepoint':
		case 'sharepointlibraryconnector':
			connectorText = 'Sharepoint';
			break;

		case 'azurefilesconnector':
			connectorText = 'Azure';
			break;
		default:
			connectorText = 'Unknown';
	}
	return connectorText;
}

export const formatWithOptions = (
	operations: FilterOperation[],
): Condition[] => {
	const operationLabels: Record<FilterOperation, string> = {
		[FilterOperation.CONTAINS]: 'Contains',
		[FilterOperation.NOT_CONTAINS]: 'Does not contain',
		[FilterOperation.BLANK]: 'Is blank',
		[FilterOperation.NOT_BLANK]: 'Is not blank',
		[FilterOperation.EQUALS]: 'Is equal to',
		[FilterOperation.GREATER]: 'After',
		[FilterOperation.BETWEEN]: 'Between',
		[FilterOperation.LESS]: 'Before',
		[FilterOperation.DATE_EQUALS]: 'Is equal to',
		[FilterOperation.IN]: 'Is in',
	};

	return operations.map((operation) => ({
		label: operationLabels[operation],
		value: operation,
	}));
};

export const formatOperationLabel = (operation: string): string => {
	const match = DETECTION_FILTER_CONDITIONS.find(
		(c) => c.value === operation,
	);
	return match ? match.label : operation;
};

type FilterChip = { label: string; value: string; onDelete?: () => void };

type FilterInput = {
	fieldName: string;
	value: string[];
	operation: string;
};

const MAX_VISIBLE_CHIP_VALUES = 3;

const allColumns = [...TAB_COLUMNS[0], ...TAB_COLUMNS[1], ...TAB_COLUMNS[2]];

const getColumnLabel = (fieldName: string): string => {
	const col = allColumns.find((c) => c.accessorKey === fieldName);
	return col?.header ?? fieldName;
};

const getConnector = (operation: string): string =>
	operation === 'Equals' ? ' AND ' : ' OR ';

export function buildDisplayString(
	searchField: string,
	searchText: string,
	filters: FilterInput[],
): string {
	const parts: string[] = [];

	if (searchField && searchText) {
		const searchLabel =
			SEARCH_FIELD_OPTIONS.find((opt) => opt.value === searchField)
				?.label ?? searchField;
		parts.push(`${searchLabel}: ${searchText}`);
	} else if (searchText) {
		parts.push(searchText);
	}

	for (const filter of filters) {
		if (!filter.value || filter.value.length === 0) continue;
		const label = getColumnLabel(filter.fieldName);
		const connector = getConnector(filter.operation);
		parts.push(`${label}: ${filter.value.join(connector)}`);
	}

	return parts.join('  &  ');
}

export function generateFilterChips(
	filters: FilterInput[],
	handleRemoveColumnFilter: (fieldName: string) => void,
): FilterChip[] {
	if (!filters || filters.length === 0) return [];

	return filters.map((filter) => {
		const { fieldName, value, operation } = filter;
		const label = getColumnLabel(fieldName);
		const opLabel = formatOperationLabel(operation);
		const connector = getConnector(operation);

		const arr = Array.isArray(value) ? value : [value];
		const visibleValues = arr.slice(0, MAX_VISIBLE_CHIP_VALUES);
		const remainingCount = arr.length - MAX_VISIBLE_CHIP_VALUES;

		let chipValue = `${opLabel} ${visibleValues.join(connector)}`;
		if (remainingCount > 0) {
			chipValue += ` +${remainingCount} more`;
		}

		return {
			label,
			value: chipValue,
			onDelete: () => handleRemoveColumnFilter(fieldName),
		};
	});
}
